/** @file bone.split's region mesh: a 3-column grid with extra rows across the cut, in slot-bone space. */
import { affine } from '@pose2d/math/affine';
import type { RegionAtt } from '@pose2d/format/types';

/** Regular rows along the bone, plus the rows at the cut and at ±½, ±1 band. */
const ROWS = 8;
const LOOPS = [-1, -0.5, 0, 0.5, 1] as const;
/** A regular row this close (in grid units) to a loop row is dropped. */
const CLEAR = 0.25 / ROWS;

/** Geometry of the new rigid mesh (attachment.createMesh fields). */
export type SplitMesh = { uvs: number[]; vertices: number[]; triangles: number[]; hull: number };

/** Region point (u, v ∈ [0, 1] of its image, y down) in the slot bone's space. */
function pointOf(r: RegionAtt, u: number, v: number): [number, number] {
  const place = affine.fromLocal([0, 0, 0, 0, 0, 0], r.x, r.y, r.rotation, r.scaleX, r.scaleY, 0, 0);
  return affine.apply(place, (u - 0.5) * r.width, (0.5 - v) * r.height, [0, 0]);
}

/** Twice the signed area (y up) of triangle a, b, c of flat points. */
const area2 = (p: readonly number[], a: number, b: number, c: number): number =>
  ((p[2 * b] ?? 0) - (p[2 * a] ?? 0)) * ((p[2 * c + 1] ?? 0) - (p[2 * a + 1] ?? 0)) - ((p[2 * c] ?? 0) - (p[2 * a] ?? 0)) * ((p[2 * b + 1] ?? 0) - (p[2 * a + 1] ?? 0));

/** Twice the signed area (y up) of the ring made of the first `count` flat points. */
function area2Ring(p: readonly number[], count: number): number {
  let s = 0;
  for (let i = 0; i < count; i++) {
    const j = (i + 1) % count;
    s += (p[2 * i] ?? 0) * (p[2 * j + 1] ?? 0) - (p[2 * j] ?? 0) * (p[2 * i + 1] ?? 0);
  }
  return s;
}

/** Row values along the bone: ROWS regular rows and the loop rows where the region's centre line crosses cut + k·band. */
function rowsOf(r: RegionAtt, alongU: boolean, cut: number, band: number): number[] {
  const at = (a: number): number => (alongU ? pointOf(r, a, 0.5) : pointOf(r, 0.5, a))[0];
  const [x0, x1] = [at(0), at(1)];
  const loops = Math.abs(x1 - x0) < 1e-9 ? [] : LOOPS.map((k) => (cut + k * band - x0) / (x1 - x0)).filter((a) => a > 0 && a < 1);
  const regular = Array.from({ length: ROWS + 1 }, (_, k) => k / ROWS).filter((a) => a === 0 || a === 1 || loops.every((l) => Math.abs(l - a) > CLEAR));
  return [...new Set([...regular, ...loops])].sort((a, b) => a - b);
}

/**
 * A region's image as a mesh for bone.split: rows run across the bone (along whichever image axis points more along
 * the bone's x), three columns across it, extra rows at cut + {−1, −½, 0, ½, 1}·band. Hull first (the outline, counter-
 * clockwise in bone space), then the middle column; every triangle counter-clockwise.
 */
export function splitGrid(r: RegionAtt, cut: number, band: number): SplitMesh {
  const du = pointOf(r, 1, 0.5)[0] - pointOf(r, 0, 0.5)[0];
  const dv = pointOf(r, 0.5, 1)[0] - pointOf(r, 0.5, 0)[0];
  const alongU = Math.abs(du) >= Math.abs(dv);
  const rows = rowsOf(r, alongU, cut, band);
  const m = rows.length;
  const cell: [number, number][] = [
    ...[0, 0.5, 1].map((c): [number, number] => [0, c]),
    ...rows.slice(1, -1).map((_, i): [number, number] => [i + 1, 1]),
    ...[1, 0.5, 0].map((c): [number, number] => [m - 1, c]),
    ...rows.slice(1, -1).map((_, i): [number, number] => [m - 2 - i, 0]),
  ];
  const hull = cell.length;
  cell.push(...rows.slice(1, -1).map((_, i): [number, number] => [i + 1, 0.5]));
  const uv = cell.map(([i, c]): [number, number] => (alongU ? [rows[i] ?? 0, c] : [c, rows[i] ?? 0]));
  const local = uv.flatMap(([u, v]) => pointOf(r, u, v));
  const order = area2Ring(local, hull) < 0 ? [...Array.from({ length: hull }, (_, k) => hull - 1 - k), ...Array.from({ length: cell.length - hull }, (_, k) => hull + k)] : cell.map((_, k) => k);
  const at = new Map(order.map((k, n) => [`${cell[k]?.[0]}/${cell[k]?.[1]}`, n]));
  const vertices = order.flatMap((k) => [local[2 * k] ?? 0, local[2 * k + 1] ?? 0]);
  const triangles: number[] = [];
  for (let i = 0; i + 1 < m; i++) {
    for (const [c0, c1] of [[0, 0.5], [0.5, 1]] as const) {
      const q = [`${i}/${c0}`, `${i + 1}/${c0}`, `${i + 1}/${c1}`, `${i}/${c1}`].map((key) => at.get(key) ?? 0);
      for (const t of [[q[0], q[1], q[2]], [q[0], q[2], q[3]]] as const) {
        const [a = 0, b = 0, c = 0] = t;
        triangles.push(...(area2(vertices, a, b, c) < 0 ? [a, c, b] : [a, b, c]));
      }
    }
  }
  return { uvs: order.flatMap((k) => uv[k] ?? [0, 0]), vertices, triangles, hull };
}
